import { useEffect, useLayoutEffect, useRef, useState } from "react";
import useSWR from "swr";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { buildPresetInitialValues } from "utils/preset-answer-helpers";
import * as Yup from "yup";
import { fetcherSimple } from "utils/api";
import { getApiErrorMessage, classifyError } from "utils/api-error";
import { toast } from "react-toastify";
import RHFSetCheckbox from "components/form/rhf-set-checkbox";
import RHFRadio from "components/form/rhf-radio";
import DeleteCustomAnswerDialog from "./dialog/delete-custom-answer-dialog";

// Recipe P6: handleSubmit rethrows anything the submit callback throws,
// so every invocation site toasts instead of failing silently.
const toastSubmitError = (err) =>
    toast.error(getApiErrorMessage(classifyError(err), "respond"));

export default function PresetQuestionResponse(props) {
    const question = props.question;
    const responses = props.responses;
    const response = props.responses.preset;

    // Tracks whether the user confirmed overwriting an existing custom answer
    // when picking a preset in single-answer mode.
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [reallyDeleteCustom, setReallyDeleteCustom] = useState(false);
    // The "confirm overwrite" flag. Deliberately a ref, not useState:
    // it is only read by the submit handler, which also runs via
    // submitRef from the confirm dialog, and a state flip would
    // re-render without the flag value being needed in the render.
    const confirmingRef = useRef(false);

    const { data: questionPresets, error: questionPresetsError } = useSWR(
        () => [`/v2/response/question/${question.nanoid}/preset`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const methods = useForm({
        mode: "onChange",
        // The array rule is scoped to the answer field. Passed bare,
        // yupResolver maps the root cast failure (array schema
        // vs object values) onto a blocking root error; that failure
        // never fires on these values, so no field errors surface and
        // nothing blocks submit.
        resolver: yupResolver(
            Yup.object().shape({ answer: Yup.array().of(Yup.string()) }),
        ),
        defaultValues: { answer: [] },
    });
    const { reset, handleSubmit } = methods;

    // Recipe P5 (enableReinitialize equivalent): re-sync the form whenever
    // the values-bearing data changes identity -- on load, and after every
    // save-triggered refetch of the saved responses.
    useEffect(() => {
        if (questionPresets && response) {
            reset(
                {
                    answer: buildPresetInitialValues(
                        questionPresets.preset_answers,
                        response,
                        question.answer_type,
                    ),
                },
                { keepErrors: true },
            );
        }
    }, [questionPresets, response, question.answer_type, reset]);

    const handlePresetSubmit = async (values) => {
        if (question.answer_type === "single") {
            if (!values.answer[0] || values.answer[0] === "") {
                // No pending answer means no pending confirmation: the
                // deny path lands here (it clears the answer before
                // submitting), and leaving the flag armed here would make
                // the NEXT pick hit the declined branch below and get
                // silently wiped instead of re-asked.
                confirmingRef.current = false;
                return null;
            }
            // User declined the "this will replace your custom answer" dialog.
            if (
                responses.custom.length > 0 &&
                !reallyDeleteCustom &&
                confirmingRef.current
            ) {
                confirmingRef.current = false;
                methods.setValue(
                    "answer",
                    new Array(questionPresets.preset_answers.length),
                    { shouldDirty: true },
                );
                setShowConfirmDialog(false);
                return;
            }
            // Picking a preset when a custom answer exists: ask first.
            if (responses.custom.length > 0 && !reallyDeleteCustom) {
                confirmingRef.current = true;
                setShowConfirmDialog(true);
                return;
            }
            setReallyDeleteCustom(false);
            confirmingRef.current = false;
            const newAnswers = questionPresets.preset_answers.filter(
                (p) => values.answer[0] === p.nanoid,
            );
            props.setPresetOther(false);
            // Avoid an unnecessary state mutation when nothing changed.
            if (
                newAnswers.length !== response.length ||
                (newAnswers[0] && newAnswers[0].nanoid !== response[0].nanoid)
            ) {
                props.setAnswers({ preset: newAnswers, custom: [] });
            }
            setShowConfirmDialog(false);
        } else {
            const newAnswers = questionPresets.preset_answers.filter((p) =>
                values.answer.some((ele) => ele === p.nanoid),
            );
            if (newAnswers.length !== response.length) {
                props.setAnswers({
                    preset: newAnswers,
                    custom: responses.custom,
                });
            }
        }
    };

    // Recipe P6: submits triggered from effects and from the delete
    // dialog must run the LATEST handlePresetSubmit closure, because
    // the confirm click flips reallyDeleteCustom and submits in the
    // same handler. The latest closure lives in a ref updated by a
    // layout effect and is resolved only when validation finishes.
    const handlePresetSubmitRef = useRef(handlePresetSubmit);
    useLayoutEffect(() => {
        handlePresetSubmitRef.current = handlePresetSubmit;
    });

    const submitRef = useRef();
    useEffect(() => {
        submitRef.current = handleSubmit((values, event) =>
            handlePresetSubmitRef.current(values, event),
        );
    }, [handleSubmit]);

    const values = methods.watch();

    return (
        <>
            {!!questionPresetsError && (
                <div>Error fetching preset answers!</div>
            )}
            {!!(questionPresets && response) && (
                <FormProvider {...methods}>
                    <form
                        className="font-body"
                        onSubmit={(event) =>
                            handleSubmit(handlePresetSubmit)(
                                event,
                            ).catch(toastSubmitError)
                        }
                    >
                        {questionPresets.preset_answers
                            .sort((a, b) => {
                                return a.position - b.position;
                            })
                            .map((singlePreset, index) => (
                                <div key={index}>
                                    {question.answer_type === "single" && (
                                        <RHFRadio
                                            className="font-body text-black"
                                            containerProps={{className: "shrink-0"}}
                                            id={`answer-${singlePreset.nanoid}`}
                                            name={`answer.0`}
                                            color="blue"
                                            label={singlePreset.text}
                                            value={singlePreset.nanoid}
                                            labelProps={{
                                                className:
                                                    "text-black",
                                            }}
                                            type="radio"
                                            checked={
                                                values.answer?.[0] ===
                                                singlePreset.nanoid
                                            }
                                        />
                                    )}
                                    {question.answer_type ===
                                        "multiple" && (
                                        <RHFSetCheckbox
                                            className="font-body text-black"
                                            containerProps={{className: "shrink-0"}}
                                            id={`answer-${singlePreset.nanoid}`}
                                            name={`answer`}
                                            color="blue"
                                            label={singlePreset.text}
                                            value={singlePreset.nanoid}
                                            type="checkbox"
                                            labelProps={{
                                                className:
                                                    "text-black",
                                            }}
                                        />
                                    )}
                                </div>
                            ))}
                        <DeleteCustomAnswerDialog
                            showDialog={showConfirmDialog}
                            setShowDialog={setShowConfirmDialog}
                            setReallyDeleteCustom={setReallyDeleteCustom}
                            question={question}
                            questionPresets={questionPresets}
                            submitRef={submitRef}
                            handleClose={() =>
                                setShowConfirmDialog(
                                    (showConfirmDialog) =>
                                        !showConfirmDialog,
                                )
                            }
                        />
                        <AutoSubmit
                            question={question}
                            questionPresets={questionPresets}
                            responses={responses}
                            showConfirmDialog={showConfirmDialog}
                            setShowConfirmDialog={setShowConfirmDialog}
                            submitRef={submitRef}
                        />
                    </form>
                </FormProvider>
            )}
        </>
    );
}

// Auto-submits the form once per actual answer change. Module scope is
// load-bearing: as an inner function every parent render produced a new
// component type, React remounted it, and the mount effect re-fired the
// submit while the form stayed dirty (issue #1124's POST storm). At
// module scope the effect runs only when the serialized answer or the
// confirm-dialog flag changes.
function AutoSubmit({
    question,
    questionPresets,
    responses,
    showConfirmDialog,
    setShowConfirmDialog,
    submitRef,
}) {
    const { watch, formState } = useFormContext();
    const { isDirty, isSubmitting } = formState;
    const autoValues = watch();
    // Key the effect on the serialized answer: watch() returns a new
    // object identity on every render, so keying on the object would
    // re-fire on unrelated formState flips (isSubmitting) and loop
    // submissions. One effect run per actual answer change.
    const answerSnapshot = JSON.stringify(autoValues.answer);

    useEffect(() => {
        if (
            (autoValues.answer[0] || autoValues.answer[0] === "") &&
            responses.custom.length > 0 &&
            responses.preset.length > 0 &&
            question.answer_type === "single"
        ) {
            setShowConfirmDialog(true);
        } else if (
            questionPresets &&
            isDirty &&
            !isSubmitting &&
            // Never auto-submit while the confirm dialog is waiting on a
            // decision: the user can pick a different preset behind the
            // open dialog, and that snapshot change must not submit
            // behind their pending choice.
            !showConfirmDialog
        ) {
            submitRef.current().catch(toastSubmitError);
        }
        // Deps: showConfirmDialog is deliberate. Dismissing the dialog
        // without deciding (backdrop/Escape) must re-fire this effect so
        // the still-pending pick is run through the decline branch and
        // wiped, instead of lingering as an unsaved selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answerSnapshot, showConfirmDialog]);
    return null;
}
